import { enumMembers, type ApiSchema } from "../../apiSchema";
import { requestJson } from "../../http/httpClient";
import { readArray, readBoolean, readInteger, readObject, readOneOf, readString } from "../../http/jsonDecoding";
import { decodeToolParameter, type ToolInterface } from "./processContentDecoders";
import { decodeRecord, oneOf, SELECTOR_KINDS, VALUE_KINDS, type SelectorKind, type ValueKind } from "./selectorDecoders";

/**
 * What steps may name, as the server declares it (W-12, P-3, P-6; authoring-ui.md AU-3):
 * selector kinds and versions with the value each renders for a tool input, the value
 * kinds, compaction methods, DMW tools and built-in tools. The forms offer only this; no
 * list is kept in the web app (NFR-13).
 */

export interface SelectorVersion {
  readonly kind: SelectorKind;
  readonly version: number;
  /** The value it renders for a tool input; `null` if it renders only text. */
  readonly valueKind: ValueKind | null;
  readonly rendersText: boolean;
}

/** What a DMW tool reads or writes (the Workflow view's context map). */
export type ToolResource = ApiSchema<"ToolResource">;

export const TOOL_RESOURCES = enumMembers<ToolResource>({
  corpus: true,
  model: true,
  reference_vocabularies: true,
  thread_history: true,
  skills: true,
  process_items: true,
  change_sets: true,
  step_output: true,
});

export interface CatalogueDmwTool {
  readonly name: string;
  readonly description: string;
  /** The output part kind it submits, if any. */
  readonly submits: ValueKind | null;
  readonly resource: ToolResource;
  /** Whether it writes: proposes or submits. */
  readonly writes: boolean;
}

export interface CompactionMethodVersion {
  readonly method: string;
  readonly version: number;
  readonly description: string;
}

export type ModelUse = ApiSchema<"ModelUse">;
export const MODEL_USES = enumMembers<ModelUse>({ none: true, embedding: true });

export interface BuiltInTool {
  /** `builtin:<name>@<version>`. */
  readonly reference: string;
  readonly interface: ToolInterface;
  readonly modelUse: ModelUse;
}

export interface StepCatalogue {
  readonly selectors: readonly SelectorVersion[];
  readonly valueKinds: readonly ValueKind[];
  readonly compactionMethods: readonly CompactionMethodVersion[];
  readonly dmwTools: readonly CatalogueDmwTool[];
  readonly builtInTools: readonly BuiltInTool[];
}

export function readStepCatalogue(projectId: string, signal?: AbortSignal): Promise<StepCatalogue> {
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}/content/step-catalogue`,
    signal,
    decodeResponse: decodeStepCatalogue,
  });
}

export function decodeStepCatalogue(body: unknown): StepCatalogue {
  const catalogue = readObject(body, "step catalogue");
  return {
    selectors: readArray(catalogue["selectors"], "selectors", (value) => {
      const selector = readObject(value, "selector kind");
      const kind = readOneOf(selector, "kind", SELECTOR_KINDS);
      return readArray(selector["versions"], "selector versions", (version) => {
        const entry = readObject(version, "selector version");
        const valueKind = entry["value_kind"];
        return { kind, version: readInteger(entry, "version"), valueKind: valueKind === null ? null : oneOf(valueKind, VALUE_KINDS, "value kind"), rendersText: readBoolean(entry, "renders_text") };
      });
    }).flat(),
    valueKinds: readArray(catalogue["value_kinds"], "value kinds", (kind) => oneOf(kind, VALUE_KINDS, "value kind")),
    compactionMethods: readArray(catalogue["compaction_methods"], "compaction methods", (value) => {
      const method = readObject(value, "compaction method");
      return { method: readString(method, "method"), version: readInteger(method, "version"), description: readString(method, "description") };
    }),
    dmwTools: readArray(catalogue["dmw_tools"], "DMW tools", (value) => {
      const tool = readObject(value, "DMW tool");
      const submits = tool["submits"];
      return {
        name: readString(tool, "name"),
        description: readString(tool, "description"),
        submits: submits === null ? null : oneOf(submits, VALUE_KINDS, "value kind"),
        resource: readOneOf(tool, "resource", TOOL_RESOURCES),
        writes: readBoolean(tool, "writes"),
      };
    }),
    builtInTools: readArray(catalogue["builtin_tools"], "built-in tools", (value) => {
      const tool = readObject(value, "built-in tool");
      const reference = readString(tool, "reference");
      const declared = readObject(tool["interface"], "tool interface");
      return {
        reference,
        interface: {
          name: reference,
          description: readString(tool, "description"),
          inputs: decodeRecord(declared["inputs"], "inputs", (input) => {
            const entry = readObject(input, "input");
            return {
              accepts: readArray(entry["accepts"], "accepts", (kind) => oneOf(kind, VALUE_KINDS, "value kind")),
              required: readBoolean(entry, "required"),
              description: typeof entry["description"] === "string" ? entry["description"] : null,
            };
          }),
          parameters: decodeRecord(declared["parameters"], "parameters", decodeToolParameter),
          // The catalogue states each output as its value kind.
          outputs: decodeRecord(declared["outputs"], "outputs", (output) => ({ kind: oneOf(output, VALUE_KINDS, "value kind"), description: null })),
        },
        modelUse: readOneOf(declared, "model_use", MODEL_USES),
      };
    }),
  };
}
